import {
  Activity,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CalendarCheck,
  CalendarClock,
  ChartNoAxesCombined,
  ClipboardCheck,
  ClipboardList,
  Database,
  Heart,
  Home,
  ListChecks,
  PanelsTopLeft,
  LogOut,
  ScrollText,
  ShieldCheck,
  UserRound,
  UserRoundCheck,
  Users,
  WalletCards,
} from "lucide-react";
import type { ReactNode } from "react";
import { hasPermission, type Permission } from "@web/config/permissions";
import {
  hasAssignedRole,
  hasOrganizationScope,
  type UserRole,
} from "@web/config/roles";

export interface NavItem {
  label: string;
  section: string;
  path?: string;
  action?: "logout";
  icon: ReactNode;
  permission: Permission;
  /** Presentation preference only; access is always controlled by permission. */
  roles: UserRole[];
  /** Additional role restriction; the module permission is still required. */
  allowedRoles?: UserRole[];
  hidden?: boolean;
}

const icon = (Icon: typeof Home) => <Icon size={20} />;
const managers: UserRole[] = ["ADMIN", "MANAGER"];
const individualSales: UserRole[] = [
  "SALES_CONSULTANT",
  "SALES_MANAGER",
  "SALES_DIRECTOR",
];
const reviewers: UserRole[] = ["ADMIN", "MANAGER", "ACCOUNTS", "COMPLIANCE"];

export const navigation: NavItem[] = [
  {
    label: "Organization",
    section: "TEAM MANAGEMENT",
    path: "/organization",
    icon: icon(Users),
    permission: "users:view",
    roles: ["ADMIN", "MANAGER"],
    allowedRoles: ["ADMIN", "MANAGER"],
  },
  {
    label: "Sales Structure",
    section: "TEAM MANAGEMENT",
    path: "/sales-reporting",
    icon: icon(Users),
    permission: "leads:view",
    roles: ["ADMIN", "MANAGER", "SALES_MANAGER", "SALES_DIRECTOR"],
    allowedRoles: ["ADMIN", "MANAGER", "SALES_MANAGER", "SALES_DIRECTOR"],
  },
  {
    label: "Dashboard",
    section: "MAIN",
    path: "/dashboard",
    icon: icon(Home),
    permission: "dashboard:view",
    roles: managers,
  },
  {
    label: "Calendar",
    section: "MAIN",
    path: "/calendar",
    icon: icon(CalendarDays),
    permission: "calendar:view",
    roles: [...managers, ...individualSales],
  },
  {
    label: "Team Leads",
    section: "LEAD MANAGEMENT",
    path: "/leads",
    icon: icon(ClipboardList),
    permission: "leads:view",
    roles: managers,
  },
  {
    label: "Follow-Ups",
    section: "LEAD MANAGEMENT",
    path: "/follow-ups",
    icon: icon(CalendarClock),
    permission: "followups:view",
    roles: managers,
  },
  {
    label: "Pipeline",
    section: "LEAD MANAGEMENT",
    path: "/pipeline",
    icon: icon(PanelsTopLeft),
    permission: "pipeline:view",
    hidden: true,
    roles: managers,
  },
  {
    label: "Meetings / Site Visits",
    section: "LEAD MANAGEMENT",
    path: "/meetings-site-visits",
    icon: icon(CalendarCheck),
    permission: "followups:view",
    roles: managers,
    hidden: true,
  },
  {
    label: "KYC Approvals",
    section: "LEAD MANAGEMENT",
    path: "/kyc-management",
    icon: icon(ShieldCheck),
    permission: "kyc:review",
    roles: reviewers,
  },
  {
    label: "Properties",
    section: "PROPERTY MANAGEMENT",
    path: "/properties",
    icon: icon(Building2),
    permission: "properties:view",
    roles: managers,
  },
  {
    label: "Listing Requests",
    section: "PROPERTY MANAGEMENT",
    path: "/tickets",
    icon: icon(ScrollText),
    permission: "tickets:view",
    roles: managers,
  },
  {
    label: "Team Members",
    section: "TEAM MANAGEMENT",
    path: "/team-management",
    icon: icon(Users),
    permission: "users:view",
    roles: managers,
    allowedRoles: ["ADMIN", "MANAGER", "SALES_MANAGER", "SALES_DIRECTOR"],
  },
  {
    label: "Lead Assignments",
    section: "TEAM MANAGEMENT",
    path: "/lead-assignments",
    icon: icon(UserRoundCheck),
    permission: "users:view",
    roles: managers,
    hidden: true,
  },
  {
    label: "Team Performance",
    section: "TEAM MANAGEMENT",
    path: "/team-performance",
    icon: icon(ChartNoAxesCombined),
    permission: "reports:view",
    roles: managers,
    hidden: true,
  },
  {
    label: "Lead Reports",
    section: "REPORTS",
    path: "/reports/leads",
    icon: icon(BarChart3),
    permission: "reports:view",
    roles: managers,
  },
  {
    label: "Sales & Revenue",
    section: "REPORTS",
    path: "/reports/sales-revenue",
    icon: icon(WalletCards),
    permission: "reports:view",
    roles: managers,
  },
  {
    label: "Agent Performance",
    section: "REPORTS",
    path: "/reports/agent-performance",
    icon: icon(ChartNoAxesCombined),
    permission: "reports:view",
    roles: managers,
  },
  {
    label: "My Profile",
    section: "ACCOUNT",
    path: "/profile",
    icon: icon(UserRound),
    permission: "profile:view",
    roles: managers,
  },
  {
    label: "Logout",
    section: "ACCOUNT",
    action: "logout",
    icon: icon(LogOut),
    permission: "profile:view",
    roles: managers,
  },
  {
    label: "Dashboard",
    section: "MAIN",
    path: "/dashboard",
    icon: icon(Home),
    permission: "dashboard:view",
    roles: individualSales,
  },
  {
    label: "Assigned Leads",
    section: "MY LEADS",
    path: "/leads",
    icon: icon(ClipboardList),
    permission: "leads:view",
    roles: individualSales,
  },
  {
    label: "Follow-Ups",
    section: "MY LEADS",
    path: "/follow-ups",
    icon: icon(CalendarClock),
    permission: "followups:view",
    roles: individualSales,
  },
  {
    label: "Pipeline",
    section: "MY LEADS",
    path: "/pipeline",
    icon: icon(PanelsTopLeft),
    permission: "pipeline:view",
    hidden: true,
    roles: individualSales,
  },
  {
    label: "Meetings / Site Visits",
    section: "MY LEADS",
    path: "/meetings-site-visits",
    icon: icon(CalendarCheck),
    permission: "followups:view",
    roles: individualSales,
    hidden: true,
  },
  {
    label: "KYC Forms",
    section: "MY LEADS",
    path: "/kyc-forms",
    icon: icon(ShieldCheck),
    permission: "leads:view",
    roles: individualSales,
    hidden: true,
  },
  {
    label: "Property List",
    section: "PROPERTIES",
    path: "/properties",
    icon: icon(Building2),
    permission: "properties:view",
    roles: individualSales,
  },
  {
    label: "My Properties",
    section: "PROPERTIES",
    path: "/my-properties",
    icon: icon(ListChecks),
    permission: "properties:view",
    roles: individualSales,
  },
  {
    label: "Favorites",
    section: "PROPERTIES",
    path: "/favorites",
    icon: icon(Heart),
    permission: "favorites:view",
    roles: individualSales,
  },
  {
    label: "Tasks",
    section: "MY WORK",
    path: "/tasks",
    icon: icon(ClipboardCheck),
    permission: "profile:view",
    roles: individualSales,
    hidden: true,
  },
  {
    label: "Notifications",
    section: "MY WORK",
    path: "/notifications",
    icon: icon(Bell),
    permission: "profile:view",
    roles: individualSales,
    hidden: true,
  },
  {
    label: "My Performance",
    section: "MY WORK",
    path: "/my-performance",
    icon: icon(ChartNoAxesCombined),
    permission: "profile:view",
    roles: individualSales,
    hidden: true,
  },
  {
    label: "My Profile",
    section: "ACCOUNT",
    path: "/profile",
    icon: icon(UserRound),
    permission: "profile:view",
    roles: individualSales,
  },
  {
    label: "Logout",
    section: "ACCOUNT",
    action: "logout",
    icon: icon(LogOut),
    permission: "profile:view",
    roles: individualSales,
  },
  {
    label: "Roles & Permissions",
    section: "SYSTEM",
    path: "/roles",
    icon: icon(ShieldCheck),
    permission: "roles:view",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    label: "My Profile",
    section: "ACCOUNT",
    path: "/profile",
    icon: icon(UserRound),
    permission: "profile:view",
    roles: ["ACCOUNTS", "COMPLIANCE"],
  },
  {
    label: "Logout",
    section: "ACCOUNT",
    action: "logout",
    icon: icon(LogOut),
    permission: "profile:view",
    roles: ["ACCOUNTS", "COMPLIANCE"],
  },
  {
    label: "Master Data",
    section: "SYSTEM",
    path: "/master-data",
    icon: icon(Database),
    permission: "master:view",
    roles: ["ADMIN", "MANAGER"],
  },
  {
    label: "Activity Logs",
    section: "SYSTEM",
    path: "/activity-logs",
    icon: icon(Activity),
    permission: "logs:view",
    roles: managers,
  },
  {
    label: "API Logs",
    section: "SYSTEM",
    path: "/api-logs",
    icon: icon(Activity),
    permission: "logs:view",
    roles: ["ADMIN", "MANAGER"],
  },
];

export function navigationForUser(user: {
  role: UserRole;
  roles?: readonly UserRole[];
  permissions?: readonly string[];
}) {
  const organizationScope = hasOrganizationScope(user.role, user.roles);
  const permitted = navigation.filter(
    (item) =>
      !item.hidden &&
      hasPermission(user.role, item.permission, user.permissions) &&
      (!item.allowedRoles ||
        item.allowedRoles.some((role) => hasAssignedRole(user, role))),
  );
  const preferredRoles = hasOrganizationScope(user.role, user.roles)
    ? managers
    : individualSales;
  const preferred = [
    ...permitted.filter((item) =>
      item.roles.some((role) => preferredRoles.includes(role)),
    ),
    ...permitted.filter(
      (item) => !item.roles.some((role) => preferredRoles.includes(role)),
    ),
  ];
  const seen = new Set<string>();
  const items = preferred
    .filter((item) => {
      const key = item.path || `${item.section}:${item.action}:${item.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((item) =>
      item.path === "/leads"
        ? {
            ...item,
            label:
              organizationScope || hasAssignedRole(user, "SALES_DIRECTOR")
                ? "Company Leads"
                : hasAssignedRole(user, "SALES_MANAGER")
                  ? "Assigned Leads"
                  : hasPermission(user.role, "leads:view_all", user.permissions)
                    ? "Company Leads"
                    : user.permissions?.includes("leads:view_team")
                      ? "Team Leads"
                      : "Assigned Leads",
            section: organizationScope ? "LEAD MANAGEMENT" : "MY LEADS",
          }
        : item,
    );
  const calendarIndex = items.findIndex((item) => item.path === "/calendar");
  const calendar =
    calendarIndex >= 0 ? items.splice(calendarIndex, 1)[0] : undefined;
  if (calendar) {
    const dashboardIndex = items.findIndex(
      (item) => item.path === "/dashboard",
    );
    items.splice(dashboardIndex + 1, 0, calendar);
  }
  const sectionOrder = organizationScope
    ? [
        "MAIN",
        "LEAD MANAGEMENT",
        "PROPERTY MANAGEMENT",
        "TEAM MANAGEMENT",
        "REPORTS",
        "SYSTEM",
        "PROPERTIES",
        "ACCOUNTS",
      ]
    : ["MAIN", "MY LEADS", "PROPERTIES", "TEAM MANAGEMENT", "ACCOUNTS"];
  const orderedItems = items.map((item) => ({
    ...item,
    section:
      item.section === "ACCOUNT"
        ? "ACCOUNTS"
        : !organizationScope && item.section === "LEAD MANAGEMENT"
          ? "MY LEADS"
          : !organizationScope && item.section === "PROPERTY MANAGEMENT"
            ? "PROPERTIES"
            : item.section,
  }));
  const sectionRank = (section: string) => {
    const index = sectionOrder.indexOf(section);
    return index >= 0 ? index : sectionOrder.length - 1.5;
  };
  return orderedItems.sort(
    (a, b) => sectionRank(a.section) - sectionRank(b.section),
  );
}
